/**
 * Store Type Categories
 * 
 * Common categories for different store types to help LLM suggest appropriate categories
 * when enriching items. These serve as reference/guidance for the LLM.
 * 
 * Categories are clean, non-overlapping, and easy for store owners to understand.
 */

import { normalizeStoreType } from './storeTypes';

// Master Category Lists
// These are comprehensive lists that can be shared across multiple store types

export const kirana_general_master: string[] = [
  // Food Staples
  'Grains & Pulses',
  'Flours & Rice',
  'Edible Oils & Ghee',
  'Spices & Masalas',
  'Salt, Sugar & Jaggery',
  'Tea & Coffee',
  'Pickles & Chutneys',
  'Dry Fruits & Nuts',
  
  // Food & Snacks
  'Snacks & Namkeen',
  'Biscuits & Bakery Items',
  'Chocolates & Confectionery',
  'Packaged & Instant Foods',
  'Breakfast & Ready Mixes',
  'Local Bakery Items',
  
  // Beverages & Cold
  'Beverages & Drinks',
  'Dairy & Refrigerated Items',
  'Frozen & Ready-to-Cook Foods',
  'Drinking Water & Ice',
  
  // Household & Cleaning
  'Cleaning & Household Essentials',
  'Detergents & Laundry Care',
  'Dishwash & Kitchen Cleaners',
  'Insect Killers & Repellents',
  
  // Personal Items
  'Personal Care & Hygiene',
  'Cosmetics & Beauty Care',
  'Baby Care',
  'Health & Wellness',
  
  // Pooja / Seasonal
  'Pooja & Religious Items',
  'Seasonal & Festival Items',
  'Wedding & Celebration Items',
  
  // Kitchenware & Storage
  'Plastic Household Items',
  'Utensils & Cookware',
  'Kitchen & Steelware',
  'Disposable Items (Plates, Cups, Foil)',
  
  // Stationery & Toys
  'Stationery & School Supplies',
  'Office & Computer Accessories',
  'Toys & Kids Items',
  'Gift & Fancy Items',
  
  // Tobacco & Convenience
  'Cigarettes & Tobacco Products',
  'Pan Masala, Gutka & Zarda',
  'Matches & Lighters',
  
  // General Utilities
  'Electrical & Household Utilities',
  'General Store Items'
];

export const hardware_ply_master: string[] = [
  // Tools
  'Hand Tools',
  'Power Tools',
  'Carpentry Tools',
  'Measuring & Marking Tools',
  
  // Fasteners & Hardware
  'Screws, Nails & Fasteners',
  'Hardware (Nails, Gutka)',
  'Door & Furniture Fittings',
  'Hinges & Channels',
  'Glass & Aluminum Hardware',
  
  // Building Materials
  'Plywood & Boards',
  'Laminates & Veneers',
  'Edge Banding & Profiles',
  'Building Material (small items)',
  'Cement & Building Items (small packs)',
  
  // Electrical & Plumbing
  'Electrical Items',
  'Plumbing Items',
  'Wires & Cables',
  'Switches & Sockets',
  
  // Paints & Finishes
  'Wall Paints',
  'Primers & Putty',
  'Waterproofing',
  'Thinners & Chemicals',
  'Brushes & Rollers',
  'Paints & Adhesives',
  
  // Adhesives & Chemicals
  'Adhesives & Chemicals',
  'Sealants & Fillers',
  
  // Safety & Workwear
  'Safety, Gloves & Workwear',
  'Safety Equipment',
  
  // Furniture Components
  'Furniture Fittings',
  'Drawer Slides & Runners',
  
  // General Hardware
  'General Hardware Items'
];

export const medical_master: string[] = [
  'Prescription Medicines',
  'OTC Medicines',
  'First Aid & Emergency',
  'Vitamins & Supplements',
  'Baby Care',
  'Adult & Personal Hygiene',
  'Skin Care & Hair Care',
  'Medical Devices (BP, Glucometer)',
  'Surgical & Dressing Items',
  'Health & Wellness'
];

export const stationery_master: string[] = [
  'Notebooks & Registers',
  'Pens & Writing Instruments',
  'Art & Craft Materials',
  'School & Student Supplies',
  'Office Supplies',
  'Files & Folders',
  'Printer & Computer Accessories',
  'Gift & Fancy Items'
];

export const mobile_electronics_master: string[] = [
  'Smartphones',
  'Mobile Accessories',
  'Chargers & Cables',
  'Earphones & Headphones',
  'Smart Gadgets (Watches, Bands)',
  'Speakers & Audio Items',
  'Repair & Spare Parts'
];

export const bakery_sweets_master: string[] = [
  'Bread & Buns',
  'Cakes & Pastries',
  'Cookies & Biscuits',
  'Sweets & Mithai',
  'Chocolates & Confectionery',
  'Frozen Snacks',
  'Beverages',
  'Baking Ingredients'
];

export const cosmetics_beauty_master: string[] = [
  'Makeup Products',
  'Skin Care',
  'Hair Care',
  'Personal Hygiene',
  'Perfumes & Deodorants',
  'Beauty Accessories',
  'Salon & Professional Items'
];

export const clothing_garments_master: string[] = [
  'Men\'s Wear',
  'Women\'s Wear',
  'Kids Wear',
  'Accessories',
  'Footwear'
];

export const footwear_master: string[] = [
  'Men\'s Footwear',
  'Women\'s Footwear',
  'Kids Footwear',
  'Shoe Care'
];

export const utensils_kitchenware_master: string[] = [
  'Cookware',
  'Serveware',
  'Storage Containers',
  'Kitchen Tools',
  'Dining Sets'
];

export const gift_toy_master: string[] = [
  'Gift Items',
  'Toys',
  'Greeting Cards',
  'Party Supplies'
];

export const paan_shop_master: string[] = [
  'Paan',
  'Tobacco Products',
  'Snacks',
  'Beverages'
];

export const tea_snacks_stall_master: string[] = [
  'Tea',
  'Coffee',
  'Snacks',
  'Biscuits',
  'Beverages'
];

export const dairy_master: string[] = [
  'Dairy & Refrigerated Items',
  'Beverages & Drinks',
  'Frozen & Ready-to-Cook Foods'
];

export const vegetable_fruit_master: string[] = [
  'Fresh Vegetables',
  'Fresh Fruits'
];

export const paint_building_master: string[] = [
  'Wall Paints',
  'Primers & Putty',
  'Waterproofing',
  'Thinners & Chemicals',
  'Brushes & Rollers',
  'Hardware (Nails, Gutka)',
  'Cement & Building Items (small packs)'
];

export const optical_eyewear_master: string[] = [
  'Eyeglasses',
  'Sunglasses',
  'Contact Lenses',
  'Lens Cleaning Solutions',
  'Eyewear Accessories',
  'Frames & Cases'
];

export const photocopy_printing_master: string[] = [
  'Printing Services',
  'Photocopy Services',
  'Lamination & Binding',
  'Stationery & Office Supplies',
  'Computer & Internet Services',
  'Document Services'
];

export const automobile_parts_master: string[] = [
  'Engine Parts',
  'Body Parts',
  'Electrical Parts',
  'Brake & Suspension',
  'Tires & Wheels',
  'Lubricants & Oils',
  'Car Accessories',
  'Bike Parts & Accessories'
];

export const dry_cleaners_laundry_master: string[] = [
  'Dry Cleaning Services',
  'Laundry Services',
  'Ironing Services',
  'Alteration Services',
  'Fabric Care Products'
];

export const jewellery_gold_master: string[] = [
  'Gold Jewellery',
  'Silver Jewellery',
  'Diamond Jewellery',
  'Artificial Jewellery',
  'Jewellery Accessories',
  'Gems & Stones'
];

export const building_materials_master: string[] = [
  'Cement & Concrete',
  'Bricks & Blocks',
  'Steel & Reinforcement',
  'Sand & Aggregates',
  'Tiles & Flooring',
  'Roofing Materials',
  'Plumbing Materials',
  'Electrical Materials',
  'Paint & Finishes'
];

// Store Type to Master List Mapping
// Maps store types to their corresponding master category lists
export const STORE_TYPE_TO_MASTER_MAP: Record<string, string[]> = {
  // Kirana / Grocery Store types -> kirana_general_master
  'kirana': kirana_general_master,
  'Kirana Store': kirana_general_master,
  'Kirana Wholesaler': kirana_general_master,
  'General Store': kirana_general_master,
  'general': kirana_general_master,
  
  // Specialized Kirana Wholesalers (subset of master)
  'Kirana Wholesaler (Ration & Masala)': [
    'Grains & Pulses',
    'Flours & Rice',
    'Edible Oils & Ghee',
    'Spices & Masalas',
    'Salt, Sugar & Jaggery',
    'Tea & Coffee',
    'Dry Fruits & Nuts'
  ],
  'Kirana Wholesaler (FMCG/Packaged Goods)': [
    'Snacks & Namkeen',
    'Biscuits & Bakery Items',
    'Chocolates & Confectionery',
    'Beverages & Drinks',
    'Packaged & Instant Foods',
    'Breakfast & Ready Mixes',
    'Cleaning & Household Essentials',
    'Personal Care & Hygiene',
    'Baby Care'
  ],
  'Kirana Vendor / Hawker': [
    'Snacks & Namkeen',
    'Biscuits & Bakery Items',
    'Beverages & Drinks',
    'Daily Essentials'
  ],
  
  // Hardware & Ply Store types -> hardware_ply_master
  'hardware': hardware_ply_master,
  'Hardware, Plywood & Paint Store': hardware_ply_master,
  'ply': hardware_ply_master,
  
  // Medical Store types -> medical_master
  'medical': medical_master,
  'Medical/Pharmacy': medical_master,
  
  // Stationery Store types -> stationery_master
  'stationery': stationery_master,
  'Stationery & Book Store': stationery_master,
  
  // Mobile & Electronics Store types -> mobile_electronics_master
  'mobile': mobile_electronics_master,
  'Electronics & Mobile Shop': mobile_electronics_master,
  
  // Bakery & Sweets Shop types -> bakery_sweets_master
  'bakery': bakery_sweets_master,
  'Sweet Shop and Bakery': bakery_sweets_master,
  
  // Cosmetics & Beauty Store types -> cosmetics_beauty_master
  'cosmetics': cosmetics_beauty_master,
  'Cosmetics & Beauty Store': cosmetics_beauty_master,
  
  // Clothing & Garments types -> clothing_garments_master
  'Clothing & Garments': clothing_garments_master,
  
  // Footwear types -> footwear_master
  'Footwear & Shoe Shop': footwear_master,
  
  // Utensils & Kitchenware types -> utensils_kitchenware_master
  'Utensils & Kitchenware Shop': utensils_kitchenware_master,
  
  // Gift & Toy Shop types -> gift_toy_master
  'Gift & Toy Shop': gift_toy_master,
  
  // Paan Shop types -> paan_shop_master
  'Paan Shop': paan_shop_master,
  
  // Tea & Snacks Stall types -> tea_snacks_stall_master
  'Tea & Snacks Stall': tea_snacks_stall_master,
  
  // Dairy types -> dairy_master
  'Dairy Items & Milk Booth': dairy_master,
  
  // Vegetable & Fruit Shop types -> vegetable_fruit_master
  'Vegetable & Fruit Shop': vegetable_fruit_master,
  
  // Paint & Building Material types -> paint_building_master
  'paint': paint_building_master,
  
  // Optical & Eyewear Store types -> optical_eyewear_master
  'Optical & Eyewear Store': optical_eyewear_master,
  
  // Photocopy, Printing & Internet Cafe types -> photocopy_printing_master
  'Photocopy, Printing & Internet Cafe': photocopy_printing_master,
  
  // Automobile Parts & Accessories types -> automobile_parts_master
  'Automobile Parts & Accessories': automobile_parts_master,
  
  // Dry Cleaners & Laundry types -> dry_cleaners_laundry_master
  'Dry Cleaners & Laundry': dry_cleaners_laundry_master,
  
  // Jewellery & Gold Shop types -> jewellery_gold_master
  'Jewellery & Gold Shop': jewellery_gold_master,
  
  // Building Materials & Cement types -> building_materials_master
  'Building Materials & Cement': building_materials_master,
  
  // Other
  'Other': ['Miscellaneous']
};

/**
 * Get suggested categories for a store type
 * 
 * This function matches store types to their appropriate categories,
 * using the defined store types from storeTypes.ts for matching.
 * Invalid store types are normalized to "General Store" before lookup.
 */
export function getSuggestedCategoriesForStoreType(storeType: string | null | undefined): string[] {
  // Normalize the store type first - this ensures invalid types fallback to "General Store"
  const normalizedStoreType = normalizeStoreType(storeType);
  
  // Try exact match first (case-sensitive)
  if (STORE_TYPE_TO_MASTER_MAP[normalizedStoreType]) {
    return STORE_TYPE_TO_MASTER_MAP[normalizedStoreType];
  }

  // Since normalizeStoreType already handles validation and fallback,
  // if we reach here, the store type is valid but not in the map
  // Fallback to general store categories
  return kirana_general_master;
}
